import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowUpRight, BookOpen, Loader2, MapPin } from "lucide-react";
import axios from "axios";
import CTASection from "../components/CTASection";
import SEO from "../components/SEO";
import { PAGE_META } from "../data/pageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_CATS = ["Tous", "Fasciathérapie", "Sport santé", "Mobilité", "Douleurs chroniques"];

export default function Blog() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [cat, setCat] = useState("Tous");
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [artsRes, catsRes] = await Promise.all([
          axios.get(`${API}/blog/articles`),
          axios.get(`${API}/blog/categories`),
        ]);
        setArticles(artsRes.data.articles || []);
        if (catsRes.data.categories?.length > 1) setCategories(catsRes.data.categories);
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error(e);
      } finally {
        setLoading(false);
      }
    })();
     
  }, []); // fetch once on mount

  const filtered = cat === "Tous" ? articles : articles.filter((a) => a.category === cat);
  const featured = filtered[0];
  const rest = filtered.slice(1);

  return (
    <>
      <SEO
        {...PAGE_META["/blog"]}
        keywords={["blog fasciathérapie", "sport santé Toulouse", "mobilité", "douleurs chroniques", "Saint-Orens", "Balma"]}
      />

      <section className="container-tight py-16">
        <div className="max-w-3xl">
          <span className="chip mb-4"><BookOpen size={12} /> Journal bien-être</span>
          <h1 className="font-serif text-5xl md:text-6xl font-medium text-[#1a1815] tracking-tight leading-[1.05]">
            Comprendre son corps, mieux le soigner
          </h1>
          <p className="mt-5 text-lg text-[#4a4642] leading-relaxed">
            Articles pratiques sur les fascias, la mobilité, le sport santé et l'accompagnement des douleurs chroniques à Toulouse et alentours.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                cat === c
                  ? "bg-[#c76d4b] text-white border-[#c76d4b]"
                  : "bg-transparent text-[#5c554c] border-[#eee5d6] hover:border-[#c76d4b] hover:text-[#c76d4b]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {loading && (
        <div className="container-tight pb-16 flex flex-col items-center justify-center gap-3 text-[#8a8580]">
          <Loader2 className="animate-spin text-[#d97757]" size={28} />
          <p className="text-sm">Chargement des articles…</p>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="container-tight pb-16 text-center py-20">
          <p className="text-[#8a8580]">Aucun article pour cette catégorie pour l'instant.</p>
        </div>
      )}

      {featured && !loading && (
        <section className="container-tight pb-16">
          <Link to={`/blog/${featured.slug}`} className="group grid lg:grid-cols-2 gap-10 bg-white rounded-[2rem] border border-[#e8e0d5] p-6 lg:p-8 hover:border-[#d97757]/40 hover:shadow-xl transition-all">
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img src={featured.cover} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-3 py-1 rounded-full bg-[#f0e4d5] text-[#b85e42] font-medium">{featured.category}</span>
                {featured.location && (
                  <span className="text-xs px-3 py-1 rounded-full bg-[#f7f2ec] text-[#4a4642] font-medium flex items-center gap-1"><MapPin size={11} /> {featured.location}</span>
                )}
                <span className="text-xs text-[#8a8580] flex items-center gap-1"><Clock size={12} /> {featured.read_time}</span>
              </div>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#1a1815] leading-tight mb-4">{featured.title}</h2>
              <p className="text-[#4a4642] leading-relaxed">{featured.excerpt}</p>
              <div className="mt-6 flex items-center gap-2 text-sm font-medium text-[#d97757] group-hover:gap-3 transition-all">
                Lire l'article <ArrowUpRight size={16} />
              </div>
            </div>
          </Link>
        </section>
      )}

      <section className="container-tight pb-12">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((a) => (
            <Link key={a.slug} to={`/blog/${a.slug}`} className="group bg-white rounded-3xl border border-[#e8e0d5] overflow-hidden hover:border-[#d97757]/40 hover:shadow-lg transition-all">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={a.cover} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[#f7f2ec] text-[#4a4642] font-medium">{a.category}</span>
                  {a.location && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-[#f0e4d5] text-[#b85e42] font-medium flex items-center gap-1"><MapPin size={10} /> {a.location}</span>
                  )}
                  <span className="text-xs text-[#8a8580] flex items-center gap-1"><Clock size={11} /> {a.read_time}</span>
                </div>
                <h3 className="font-serif text-xl font-medium text-[#1a1815] leading-snug mb-2 group-hover:text-[#d97757] transition-colors">{a.title}</h3>
                <p className="text-sm text-[#4a4642] leading-relaxed line-clamp-3">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CTASection />
    </>
  );
}
